import React from 'react';
import { useApi } from '@/api/ApiContext';
import { getImageDisplayUrl } from '@/api/handlers/resources/images';
import type { SupplierListQuery } from '@/api/handlers/resources/suppliers/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { SupplierDetailsItem } from '@/api/models';
import {
    useCreateSupplierPaymentMutation,
    useDeleteSupplierMutation,
    useReceivedInvoicesQuery,
    useSetSupplierArchivedMutation,
    useSupplierDetailsQuery,
    useUpdateSupplierMutation,
} from '@/api/queries';
import { useDeleteConfirm } from '@/components/deleteConfirmDialog';
import { getUser } from '@/utils';
import { useCreateSupplier } from './useCreateSupplier';
import {
    SUPPLIERS_PAGE_SIZE,
    outstandingOf,
    type Supplier,
} from './components/suppliersMeta';
import type {
    SupplierFormInitialValues,
    SupplierFormSubmit,
} from './components/supplierFormModal/useSupplierForm';
import type {
    SupplierPaymentInvoiceOption,
    SupplierPaymentSubmit,
} from './components/supplierPaymentModal/useSupplierPaymentForm';

/** Debounce window for the search box before a new request fires. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Today as ISO `YYYY-MM-DD` in the browser's zone. Built from the local date
 * parts rather than `toISOString()`, which converts to UTC first and so names
 * yesterday for anyone east of Greenwich late in the evening — exactly the
 * hours a shop is closing its books.
 */
const todayIso = (): string => {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

export type UseSuppliersPageResult = {
    /** True when the signed-in user may withdraw a payment (OrgAdmin only). */
    canDeletePayments: boolean;
    /** Drives the delete confirmation, including a refusal and its archive path. */
    deleteConfirm: ReturnType<typeof useDeleteConfirm>;
    includeArchived: boolean;
    onToggleArchived: (next: boolean) => void;
    onSetArchived: (id: string, archived: boolean) => void;
    data: Supplier[] | null;
    editingInitialValues: SupplierFormInitialValues | null;
    error: ErrorResponse | null;
    formError: string | null;
    formMode: 'create' | 'edit';
    isFormOpen: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    onAddSupplier: () => void;
    onCloseForm: () => void;
    onDeleteSupplier: (id: string) => void;
    onEditSupplier: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRetry: () => void;
    onSearchChange: (value: string) => void;
    onSubmitSupplier: (input: SupplierFormSubmit) => void;
    isPaymentOpen: boolean;
    isPaymentSubmitting: boolean;
    onClosePayment: () => void;
    onCloseDetail: () => void;
    onRecordPayment: (id: string) => void;
    onSubmitPayment: (input: SupplierPaymentSubmit) => void;
    onViewSupplier: (id: string) => void;
    paymentError: string | null;
    paymentInvoices: SupplierPaymentInvoiceOption[];
    /** The supplier the payment modal is recording against, or null. */
    paymentSupplier: Supplier | null;
    /** The supplier the detail drawer is showing, or null. */
    detailSupplier: Supplier | null;
    /** Today, ISO `YYYY-MM-DD` — the payment form's default and upper bound. */
    today: string;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

/**
 * Drives the Suppliers page. The list is **server-paginated** (`/suppliers/details`
 * takes page / pageSize / search and returns the matching page plus totalCount
 * / totalPages, BOS-81), so search and paging are pushed to the API rather than
 * done client-side — the whole supplier set is never loaded at once.
 *
 * Create is one multipart POST; edit goes through `POST /images?purpose=suppliers`
 * first when the user picked a fresh image, then PUT with the resolved URL. All
 * writes invalidate the cached pages (and the picker).
 */
export const useSuppliersPage = (): UseSuppliersPageResult => {
    const { apiClient, baseUrl } = useApi();

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(SUPPLIERS_PAGE_SIZE);
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    const [includeArchived, setIncludeArchived] = React.useState(false);

    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [isEditUploading, setIsEditUploading] = React.useState(false);

    const [detailId, setDetailId] = React.useState<string | null>(null);
    const [paymentId, setPaymentId] = React.useState<string | null>(null);
    const [paymentError, setPaymentError] = React.useState<string | null>(null);

    // Debounce the term that actually drives the request so we don't fire a
    // query per keystroke.
    React.useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedSearch(search),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(handle);
    }, [search]);

    // A new search starts from page 1.
    React.useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const query: SupplierListQuery = {
        page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
        includeArchived: includeArchived || undefined,
    };
    const suppliersQuery = useSupplierDetailsQuery(query);

    const response =
        suppliersQuery.data && suppliersQuery.data.success
            ? suppliersQuery.data.response
            : null;

    // Raw items as the server returned them — kept verbatim so the edit handler
    // can seed the form with the *original* relative imageUrl.
    const rawItems: SupplierDetailsItem[] | null = response?.items ?? null;

    const data: Supplier[] | null = React.useMemo(() => {
        if (rawItems === null) {
            return null;
        }
        return rawItems.map((item) => ({
            ...item,
            imageUrl: item.imageUrl
                ? (getImageDisplayUrl(item.imageUrl, baseUrl) ?? null)
                : null,
        }));
    }, [baseUrl, rawItems]);

    const error: ErrorResponse | null =
        suppliersQuery.data && !suppliersQuery.data.success
            ? suppliersQuery.data.error
            : null;

    const editingRaw = React.useMemo(() => {
        if (editingId === null || rawItems === null) {
            return null;
        }
        return rawItems.find((item) => item.id === editingId) ?? null;
    }, [editingId, rawItems]);

    const editingInitialValues: SupplierFormInitialValues | null =
        React.useMemo(() => {
            if (editingRaw === null) {
                return null;
            }
            return {
                name: editingRaw.name,
                serialNumber: editingRaw.serialNumber ?? '',
                phone: editingRaw.phone ?? '',
                email: editingRaw.email ?? '',
                image: editingRaw.imageUrl
                    ? {
                          imageName: editingRaw.name,
                          imageUrl:
                              getImageDisplayUrl(
                                  editingRaw.imageUrl,
                                  baseUrl,
                              ) ?? editingRaw.imageUrl,
                      }
                    : null,
            };
        }, [baseUrl, editingRaw]);

    // The payment modal offers the supplier's received invoices as allocation
    // targets, and only fetches them once one is chosen — the list is a
    // supplier-scoped read, so there is nothing to ask for before then.
    const invoicesQuery = useReceivedInvoicesQuery(paymentId);

    const paymentInvoices: SupplierPaymentInvoiceOption[] =
        React.useMemo(() => {
            if (!invoicesQuery.data || !invoicesQuery.data.success) {
                return [];
            }
            return (
                invoicesQuery.data.response.items
                    .map((invoice) => ({
                        id: invoice.id,
                        invoiceNumber: invoice.invoiceNumber,
                        outstandingTetri: outstandingOf(
                            invoice.payableTotalTetri,
                            invoice.paidAmountTetri,
                        ),
                    }))
                    // A fully-allocated invoice is not a target: allocating to it would
                    // overpay one document while the supplier's balance says otherwise.
                    .filter((invoice) => invoice.outstandingTetri > 0)
            );
        }, [invoicesQuery.data]);

    const detailSupplier = React.useMemo(
        () => data?.find((item) => item.id === detailId) ?? null,
        [data, detailId],
    );

    const paymentSupplier = React.useMemo(
        () => data?.find((item) => item.id === paymentId) ?? null,
        [data, paymentId],
    );

    const { create, isCreating } = useCreateSupplier();
    const createPaymentMutation = useCreateSupplierPaymentMutation();
    const updateMutation = useUpdateSupplierMutation();
    const deleteMutation = useDeleteSupplierMutation();

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onPageSizeChange = React.useCallback((next: number) => {
        setPageSize(next);
        setPage(1);
    }, []);

    const onAddSupplier = React.useCallback(() => {
        setEditingId(null);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onEditSupplier = React.useCallback((id: string) => {
        setEditingId(id);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onCloseForm = React.useCallback(() => {
        setIsFormOpen(false);
        setEditingId(null);
        setFormError(null);
    }, []);

    const archiveMutation = useSetSupplierArchivedMutation();

    // The dialog owns the refusal: a delete can now come back with a sentence
    // naming what depends on the supplier, and archiving is the way out.
    const deleteConfirm = useDeleteConfirm({
        onDelete: (id) => deleteMutation.mutateAsync(id),
        onArchive: (id) => archiveMutation.mutateAsync({ id, archived: true }),
    });

    const onDeleteSupplier = React.useCallback(
        (id: string) => {
            const supplier = data?.find((item) => item.id === id);
            deleteConfirm.onRequestDelete({
                id,
                name: supplier?.name ?? id,
            });
        },
        [data, deleteConfirm],
    );

    const onSetArchived = React.useCallback(
        (id: string, archived: boolean) => {
            archiveMutation.mutate({ id, archived });
        },
        [archiveMutation],
    );

    const onToggleArchived = React.useCallback((next: boolean) => {
        setIncludeArchived(next);
        setPage(1);
    }, []);

    const onRetry = React.useCallback(() => {
        suppliersQuery.refetch();
    }, [suppliersQuery]);

    const onViewSupplier = React.useCallback((id: string) => {
        setDetailId(id);
    }, []);

    const onCloseDetail = React.useCallback(() => {
        setDetailId(null);
    }, []);

    const onRecordPayment = React.useCallback((id: string) => {
        setPaymentError(null);
        setPaymentId(id);
    }, []);

    const onClosePayment = React.useCallback(() => {
        setPaymentId(null);
        setPaymentError(null);
    }, []);

    const onSubmitPayment = React.useCallback(
        async (input: SupplierPaymentSubmit) => {
            if (paymentId === null) {
                return;
            }
            setPaymentError(null);
            const result = await createPaymentMutation.mutateAsync({
                supplierId: paymentId,
                body: { ...input, branchId: null },
            });
            if (!result.success) {
                // Kept open on failure so the server's reason — a closed
                // period, an untagged bank account — is readable and nothing
                // has to be re-entered.
                setPaymentError(result.error.message ?? null);
                return;
            }
            setPaymentId(null);
        },
        [createPaymentMutation, paymentId],
    );

    const onSubmitSupplier = React.useCallback(
        async (input: SupplierFormSubmit) => {
            setFormError(null);

            if (editingId === null) {
                // ---- Create flow (single multipart round-trip) ------------
                // Shared with the Control Center's "Add supplier", which opens
                // this same form from the launcher — see `useCreateSupplier`.
                const created = await create(input);
                if (!created.ok) {
                    setFormError(created.message);
                    return;
                }
                setIsFormOpen(false);
                return;
            }

            // ---- Edit flow ----------------------------------------------
            let nextImageUrl: string | null = input.imageUrl;
            if (input.imageUpload) {
                setIsEditUploading(true);
                try {
                    const uploadResult = await apiClient.images.uploadImage(
                        input.imageUpload,
                        'suppliers',
                    );
                    if (!uploadResult.success) {
                        setFormError(
                            uploadResult.error.message ??
                                'Failed to upload image.',
                        );
                        return;
                    }
                    nextImageUrl = uploadResult.response.imageUrl;
                } finally {
                    setIsEditUploading(false);
                }
            }

            const updateResult = await updateMutation.mutateAsync({
                id: editingId,
                body: {
                    name: input.name,
                    serialNumber: input.serialNumber,
                    phone: input.phone,
                    email: input.email,
                    imageUrl: nextImageUrl,
                },
            });
            if (!updateResult.success) {
                setFormError(
                    updateResult.error.message ?? 'Failed to update supplier.',
                );
                return;
            }
            setEditingId(null);
            setIsFormOpen(false);
        },
        [apiClient, create, editingId, updateMutation],
    );

    return {
        canDeletePayments: getUser()?.role === 'OrgAdmin',
        data,
        deleteConfirm,
        includeArchived,
        onToggleArchived,
        onSetArchived,
        detailSupplier,
        editingInitialValues,
        error,
        formError,
        formMode: editingId === null ? 'create' : 'edit',
        isFormOpen,
        isLoading: suppliersQuery.isLoading,
        isSubmitting: isCreating || updateMutation.isPending || isEditUploading,
        onAddSupplier,
        onCloseForm,
        onDeleteSupplier,
        onEditSupplier,
        onPageChange,
        onPageSizeChange,
        onRetry,
        onSearchChange,
        onSubmitSupplier,
        isPaymentOpen: paymentId !== null,
        isPaymentSubmitting: createPaymentMutation.isPending,
        onClosePayment,
        onCloseDetail,
        onRecordPayment,
        onSubmitPayment,
        onViewSupplier,
        paymentError,
        paymentInvoices,
        paymentSupplier,
        today: todayIso(),
        page,
        pageSize,
        search,
        totalCount: response?.totalCount ?? 0,
        totalPages: response?.totalPages ?? 1,
    };
};
